// app_client/src/components/modals/TransactionModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';
import { X, ArrowUpRight, ArrowDownLeft, User, Loader2 } from 'lucide-react';
import useTransactionStore from '../../store/useTransactionStore';
import useFinancialStore from '../../store/useFinancialStore';
import useExchangeStore from '../../store/useExchangeStore';
import AccountSelectModal from './AccountSelectModal';
import { formatCurrency } from '../../utils/formatters';

const emptyInitialData = {};
const currencySymbols = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };
const normalizeAmount = (amount) => (
  amount == null || amount === '' || !Number.isFinite(Number(amount)) ? '' : Number(amount)
);
const personnelCategories = ['Personel Ödemesi', 'Maaş/Yevmiye'];

const TransactionModal = ({
  isOpen,
  onClose,
  accountId = null,
  initialData = emptyInitialData
}) => {
  const { addTransaction, addTransfer, getAvailableInvoices } = useTransactionStore();
  const { accounts: financialAccounts, fetchAccounts } = useFinancialStore();
  const { fetchRates } = useExchangeStore();

  const [isAccountSearchOpen, setIsAccountSearchOpen] = useState(false);
  const [selectedCari, setSelectedCari] = useState(initialData.account || null);
  const [formError, setFormError] = useState('');
  const [exchangeRate, setExchangeRate] = useState('1');
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const [formData, setFormData] = useState({
    accountId,
    financialAccountId: '',
    targetFinancialAccountId: '',
    type: 'Inflow',
    category: accountId ? 'Trade' : 'Adjustment',
    allocations: [],
    operation: null,
    personelAccountId: null,
    description: '',
    date: new Date().toISOString().split('T')[0],
    ...initialData,
    amount: normalizeAmount(initialData.amount)
  });

  const isTransfer = formData.category === 'Transfer';
  const isPersonnelTransaction = personnelCategories.includes(formData.category);
  const isTrade = formData.category === 'Trade' || isPersonnelTransaction;

  useEffect(() => {
    if (!isOpen) return;

    if (financialAccounts.length === 0) {
      fetchAccounts();
    }

    const resetTimer = setTimeout(() => {
      setSelectedCari(initialData.account || null);
      setFormError('');

      setFormData({
        accountId: initialData.accountId ?? accountId,
        financialAccountId: initialData.financialAccountId || '',
        targetFinancialAccountId: initialData.targetFinancialAccountId || '',
        amount: normalizeAmount(initialData.amount),
        type: initialData.type || 'Inflow',
        category: initialData.category || (accountId ? 'Trade' : 'Adjustment'),
        allocations: initialData.allocations || [],
        operation: initialData.operation || null,
        personelAccountId: initialData.personelAccountId || null,
        description: initialData.description || '',
        date: initialData.date || new Date().toISOString().split('T')[0],
        exchangeRate: initialData.exchangeRate || ''
      });
      setExchangeRate(initialData.exchangeRate ? String(initialData.exchangeRate) : '1');
      setSelectedInvoiceIds((initialData.allocations || []).map((allocation) => String(allocation.invoiceId || allocation)));
    }, 0);

    return () => clearTimeout(resetTimer);
  }, [isOpen, fetchAccounts, accountId, initialData, financialAccounts.length]);

  const currentFinancialAccount = useMemo(
    () => financialAccounts.find((acc) => acc._id === formData.financialAccountId),
    [formData.financialAccountId, financialAccounts]
  );

  const displayCurrency = useMemo(
    () => currentFinancialAccount?.currency || initialData.currency || 'TRY',
    [currentFinancialAccount, initialData.currency]
  );

  const allocationAccountId = formData.accountId || accountId;
  const compatibleOpenInvoices = useMemo(() => openInvoices.filter((invoice) => {
    if (formData.type === 'Inflow') return ['Sales', 'SalesReturn'].includes(invoice.type);
    return ['Purchase', 'PurchaseReturn'].includes(invoice.type);
  }), [formData.type, openInvoices]);

  useEffect(() => {
    if (!isOpen || !isTrade || !allocationAccountId) {
      setOpenInvoices([]);
      setSelectedInvoiceIds([]);
      return;
    }

    let active = true;
    const loadInvoices = async () => {
      setInvoicesLoading(true);
      try {
        const invoices = await getAvailableInvoices(allocationAccountId);
        if (active) setOpenInvoices(invoices);
      } catch {
        if (active) setOpenInvoices([]);
      } finally {
        if (active) setInvoicesLoading(false);
      }
    };
    loadInvoices();
    return () => { active = false; };
  }, [allocationAccountId, getAvailableInvoices, isOpen, isTrade]);

  useEffect(() => {
    const visibleIds = new Set(compatibleOpenInvoices.map((invoice) => String(invoice._id)));
    setSelectedInvoiceIds((previous) => previous.filter((invoiceId) => visibleIds.has(String(invoiceId))));
  }, [compatibleOpenInvoices]);

  const waterfallPreview = useMemo(() => {
    let remainingSourceAmount = Number(formData.amount || 0);
    const sourceTryRate = displayCurrency === 'TRY' ? 1 : Number(exchangeRate || 0);
    const selectedInvoices = selectedInvoiceIds
      .map((invoiceId) => compatibleOpenInvoices.find((invoice) => String(invoice._id) === String(invoiceId)))
      .filter(Boolean);

    const allocations = selectedInvoices.map((invoice) => {
      const invoiceCurrency = invoice.currency || 'TRY';
      const invoiceTryRate = invoiceCurrency === 'TRY' ? 1 : Number(invoice.exchangeRate || 0);
      const crossRate = displayCurrency === invoiceCurrency ? 1 : sourceTryRate / invoiceTryRate;
      const invoiceRemainingAmount = Number(invoice.remainingAmount ?? invoice.grandTotal ?? 0);
      const invoiceAmount = crossRate > 0 ? Math.min(invoiceRemainingAmount, remainingSourceAmount * crossRate) : 0;
      const sourceAmount = crossRate > 0 ? invoiceAmount / crossRate : 0;
      remainingSourceAmount = Math.max(0, remainingSourceAmount - sourceAmount);

      return { invoice, sourceAmount, invoiceAmount, crossRate };
    });

    return { allocations, remainingSourceAmount: Math.max(0, remainingSourceAmount) };
  }, [compatibleOpenInvoices, displayCurrency, exchangeRate, formData.amount, selectedInvoiceIds]);

  useEffect(() => {
    let active = true;

    const hydrateExchangeRate = async () => {
      if (displayCurrency === 'TRY') {
        setExchangeRate('1');
        return;
      }

      setExchangeRateLoading(true);
      try {
        const rates = await fetchRates();
        if (!active) return;
        const nextRate = Number(rates?.[displayCurrency] || 1);
        setExchangeRate(Number.isFinite(nextRate) && nextRate > 0 ? String(nextRate) : '1');
      } finally {
        if (active) setExchangeRateLoading(false);
      }
    };

    hydrateExchangeRate();

    return () => {
      active = false;
    };
  }, [displayCurrency, formData.date, fetchRates]);

  const accountSelectType = isPersonnelTransaction ? 'EMPLOYEE' : 'CUSTOMER';
  const accountSelectTitle = isPersonnelTransaction ? 'Personel Seçimi' : 'Cari Seçimi';

  const handleCategoryChange = (category) => {
    setSelectedCari(null);
    setIsAccountSearchOpen(false);
    setFormError('');

    setFormData((prev) => ({
      ...prev,
      category,
      accountId: null,
      personelAccountId: null,
      allocations: []
    }));
  };

  const handleAccountSelect = (account) => {
    setSelectedCari(account);
    setFormError('');

    setFormData((prev) => ({
      ...prev,
      accountId: account._id,
      personelAccountId: isPersonnelTransaction ? account._id : null,
      allocations: []
    }));
  };

  const clearSelectedAccount = () => {
    setSelectedCari(null);
    setFormError('');

    setFormData((prev) => ({
      ...prev,
      accountId: null,
      personelAccountId: null,
      allocations: []
    }));
  };

  const handleClose = () => {
    setIsAccountSearchOpen(false);
    setFormError('');
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isTrade && !formData.accountId) {
      setFormError('Lütfen işlem yapılacak Cariyi seçiniz.');
      return;
    }

    if (!formData.financialAccountId) {
      setFormError('Lütfen ilgili kasa / banka seçiniz.');
      return;
    }

    if (isTransfer && !formData.targetFinancialAccountId) {
      setFormError('Lütfen hedef hesabı seçiniz.');
      return;
    }

    setFormError('');

    const action = isTransfer ? addTransfer : addTransaction;

    const payload = {
      ...formData,
      amount: Number(formData.amount),
      exchangeRate: displayCurrency === 'TRY' ? 1 : Number(exchangeRate || 1),
      allocations: waterfallPreview.allocations.map(({ invoice, sourceAmount }) => ({
        invoiceId: invoice._id,
        amount: sourceAmount
      }))
    };

    const result = await action(payload);

    if (result.success) {
      handleClose();
    } else {
      setFormError(result.message || 'İşlem kaydedilemedi.');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
        <div className="w-full max-w-5xl overflow-hidden rounded-[2rem] border border-white/20 bg-white shadow-2xl animate-in fade-in zoom-in duration-200">

          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/70 px-6 py-5 md:px-8">
            <div>
              <h2 className="text-xl font-black tracking-tight text-slate-800">
                {isTransfer ? 'Virman' : isTrade ? 'Cari İşlem' : 'Finansal Kayıt'}
              </h2>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                {accountId ? 'Cari Sayfasından Hızlı İşlem' : 'Merkezi Finans Yönetimi'}
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-200"
            >
              <X size={20} />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="grid max-h-[82vh] gap-6 overflow-y-auto px-6 py-6 md:grid-cols-2 md:px-8 md:py-8"
          >
            {!accountId && (
              <div className="md:col-span-2">
                <label className="ml-1 text-[10px] font-black uppercase italic tracking-widest text-slate-400">
                  İşlem Türü
                </label>

                <select
                  value={formData.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="mt-1 w-full rounded-2xl border-none bg-slate-50 px-4 py-4 text-sm font-bold text-slate-700 outline-none transition-all focus:ring-2 focus:ring-slate-100"
                >
                  <option value="Adjustment">Genel Gider / Düzeltme</option>
                  <option value="Trade">Cari Hesap İşlemi (Tahsilat/Ödeme)</option>
                  <option value="Personel Ödemesi">Personel Ödemesi</option>
                  <option value="Maaş/Yevmiye">Maaş/Yevmiye</option>
                  <option value="Transfer">Virman (Hesaplar Arası)</option>
                  <option value="Opening">Açılış Bakiyesi</option>
                </select>
              </div>
            )}

            {isTrade && !accountId && (
              <div className="md:col-span-2 animate-in slide-in-from-top-2">
                <label className="ml-1 text-[10px] font-black uppercase italic tracking-widest text-slate-400">
                  {isPersonnelTransaction ? 'Personel Carisi' : 'Cari (Müşteri/Firma)'}
                </label>

                <div className="mt-1 grid gap-3 md:grid-cols-[160px_minmax(0,1fr)]">
                  <button
                    type="button"
                    onClick={() => setIsAccountSearchOpen(true)}
                    className="rounded-2xl bg-indigo-600 px-4 py-4 text-[10px] font-black uppercase tracking-widest text-white transition-all hover:bg-indigo-700 active:scale-95"
                  >
                    {selectedCari ? 'Cari Değiştir' : 'Cari Seç'}
                  </button>

                  {selectedCari ? (
                    <div className="flex min-w-0 items-center justify-between gap-3 rounded-2xl border-2 border-indigo-100 bg-indigo-50/70 px-4 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="rounded-xl bg-white p-2 text-indigo-500 shadow-sm">
                          <User size={16} />
                        </div>

                        <div className="min-w-0">
                          <p className="truncate text-xs font-black text-slate-800">
                            {selectedCari.name}
                          </p>
                          <p className="truncate text-[10px] font-bold uppercase text-slate-400">
                            {selectedCari.code || 'Kodsuz'}
                            {selectedCari.type ? ` - ${selectedCari.type}` : ''}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={clearSelectedAccount}
                        className="rounded-full bg-white p-1.5 text-indigo-500 shadow-sm transition-colors hover:bg-indigo-100"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center rounded-2xl border-2 border-slate-100 bg-slate-50 px-4 py-4">
                      <p className="text-xs font-bold text-slate-400">
                        {isPersonnelTransaction ? 'Personel seçilmedi' : 'Cari seçilmedi'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!isTransfer && (
              <div className="md:col-span-2 flex flex-col gap-3 rounded-2xl bg-slate-100 p-1 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, type: 'Inflow' }))}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-[10px] font-black uppercase transition-all ${
                    formData.type === 'Inflow'
                      ? 'bg-emerald-500 text-white shadow-lg'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  <ArrowDownLeft size={14} />
                  Tahsilat (Giriş)
                </button>

                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, type: 'Outflow' }))}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-[10px] font-black uppercase transition-all ${
                    formData.type === 'Outflow'
                      ? 'bg-rose-500 text-white shadow-lg'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  <ArrowUpRight size={14} />
                  Ödeme (Çıkış)
                </button>
              </div>
            )}

            <div>
              <label className="ml-1 text-[10px] font-black uppercase italic tracking-widest text-slate-400">
                {isTransfer ? 'Kaynak Hesap' : 'İlgili Kasa / Banka'}
              </label>

              <select
                value={formData.financialAccountId}
                onChange={(e) => {
                  setFormError('');
                  setFormData((prev) => ({
                    ...prev,
                    financialAccountId: e.target.value,
                    targetFinancialAccountId:
                      prev.targetFinancialAccountId === e.target.value
                        ? ''
                        : prev.targetFinancialAccountId
                  }));
                }}
                required
                className="mt-1 w-full rounded-2xl border-none bg-slate-50 px-4 py-4 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-slate-100"
              >
                <option value="">Hesap Seçiniz...</option>

                {financialAccounts.map((acc) => (
                  <option key={acc._id} value={acc._id}>
                    {acc.name} ({acc.currency})
                  </option>
                ))}
              </select>
            </div>

            {isTransfer && (
              <div className="animate-in slide-in-from-top-2">
                <label className="ml-1 text-[10px] font-black uppercase italic tracking-widest text-slate-400">
                  Hedef Hesap
                </label>

                <select
                  value={formData.targetFinancialAccountId}
                  onChange={(e) => {
                    setFormError('');
                    setFormData((prev) => ({
                      ...prev,
                      targetFinancialAccountId: e.target.value
                    }));
                  }}
                  required
                  className="mt-1 w-full rounded-2xl border-2 border-indigo-100 bg-indigo-50/50 px-4 py-4 text-sm font-bold text-slate-700 outline-none"
                >
                  <option value="">Hedef Seçiniz...</option>

                  {financialAccounts
                    .filter((acc) => acc._id !== formData.financialAccountId)
                    .map((acc) => (
                      <option key={acc._id} value={acc._id}>
                        {acc.name} ({acc.currency})
                      </option>
                    ))}
                </select>
              </div>
            )}

            <div className={isTransfer ? 'md:col-span-2' : ''}>
              <label className="ml-1 text-[10px] font-black uppercase italic tracking-widest text-slate-400">
                Tutar
              </label>

              <div className="mt-1 grid grid-cols-[minmax(0,1fr)_96px] gap-3">
                <NumericFormat
                  type="text"
                  inputMode="decimal"
                  aria-label="Tutar"
                  required
                  placeholder={`0,00 ${currencySymbols[displayCurrency] || displayCurrency}`}
                  thousandSeparator="."
                  decimalSeparator=","
                  allowedDecimalSeparators={[',']}
                  decimalScale={2}
                  fixedDecimalScale
                  suffix={` ${currencySymbols[displayCurrency] || displayCurrency}`}
                  value={formData.amount}
                  onValueChange={({ floatValue }) => setFormData((prev) => ({
                    ...prev,
                    amount: floatValue ?? ''
                  }))}
                  onFocus={(e) => {
                    if (formData.amount === 0) e.target.select();
                  }}
                  className="min-w-0 rounded-2xl bg-slate-50 px-4 py-5 text-2xl font-black outline-none transition-all focus:ring-2 focus:ring-slate-100"
                />

                <div className="flex items-center justify-center rounded-2xl bg-slate-100 px-4 py-5 text-sm font-black tracking-[0.2em] text-slate-500 shadow-inner">
                  {displayCurrency}
                </div>
              </div>
            </div>

            {displayCurrency !== 'TRY' && (
              <div className="md:col-span-2">
                <label className="ml-1 text-[10px] font-black uppercase italic tracking-widest text-slate-400">
                  Döviz Kuru (Örn: {displayCurrency}/TL)
                </label>
                <div className="mt-1 grid grid-cols-[minmax(0,1fr)_auto] gap-3">
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={exchangeRate}
                    onChange={(e) => setExchangeRate(e.target.value)}
                    className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm font-black text-slate-700 outline-none transition-all focus:ring-2 focus:ring-slate-100"
                  />
                  <div className="flex items-center justify-center rounded-2xl bg-slate-100 px-4 py-4 text-xs font-black tracking-[0.2em] text-slate-500 shadow-inner">
                    {exchangeRateLoading ? <Loader2 size={14} className="animate-spin" /> : 'TL'}
                  </div>
                </div>
              </div>
            )}

            {isTrade && allocationAccountId && !isTransfer && (
              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <label className="text-[10px] font-black uppercase italic tracking-widest text-slate-400">
                      Açık Faturalar (Çoklu Seçim)
                    </label>
                    <p className="mt-1 text-xs font-semibold text-slate-500">
                      Seçim sırasına göre otomatik dağıtılır. Yalnız işlem yönüyle uyumlu faturalar gösterilir.
                    </p>
                  </div>
                  <span className="rounded-xl bg-white px-3 py-2 text-xs font-black text-slate-600 shadow-sm">
                    {selectedInvoiceIds.length} fatura seçildi
                  </span>
                </div>

                <div className="mt-4 max-h-56 space-y-2 overflow-y-auto pr-1">
                  {invoicesLoading ? (
                    <div className="flex items-center gap-2 py-4 text-xs font-bold text-slate-400"><Loader2 size={14} className="animate-spin" /> Faturalar yükleniyor...</div>
                  ) : compatibleOpenInvoices.length > 0 ? compatibleOpenInvoices.map((invoice) => {
                    const selectedIndex = selectedInvoiceIds.indexOf(String(invoice._id));
                    const preview = waterfallPreview.allocations.find(({ invoice: item }) => String(item._id) === String(invoice._id));
                    return (
                      <label key={invoice._id} className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border p-3 ${selectedIndex >= 0 ? 'border-indigo-200 bg-indigo-50' : 'border-slate-100 bg-white'}`}>
                        <span className="flex min-w-0 items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedIndex >= 0}
                            onChange={() => setSelectedInvoiceIds((previous) => (
                              selectedIndex >= 0
                                ? previous.filter((invoiceId) => invoiceId !== String(invoice._id))
                                : [...previous, String(invoice._id)]
                            ))}
                            className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                          />
                          <span className="min-w-0">
                            <span className="block truncate text-xs font-black text-slate-800">{invoice.invoiceNumber || 'Numarasız fatura'}</span>
                            <span className="block text-[10px] font-bold text-slate-400">Kalan: {formatCurrency(Number(invoice.remainingAmount ?? invoice.grandTotal ?? 0), invoice.currency || 'TRY')}</span>
                          </span>
                        </span>
                        <span className="shrink-0 text-right text-[10px] font-black text-indigo-700">
                          {selectedIndex >= 0 ? `#${selectedIndex + 1} · ${formatCurrency(preview?.invoiceAmount || 0, invoice.currency || 'TRY')}` : ''}
                        </span>
                      </label>
                    );
                  }) : (
                    <p className="py-4 text-xs font-semibold text-slate-400">Bu işlem yönü için eşleştirilebilir açık fatura yok.</p>
                  )}
                </div>

                {selectedInvoiceIds.length > 0 && (
                  <div className={`mt-4 rounded-xl px-4 py-3 text-xs font-bold ${waterfallPreview.remainingSourceAmount > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {waterfallPreview.remainingSourceAmount > 0
                      ? `Seçilen ${selectedInvoiceIds.length} fatura otomatik dağıtımla kapatılacaktır. Kalan ${formatCurrency(waterfallPreview.remainingSourceAmount, displayCurrency)} tutar carinin serbest bakiyesinde (avans) kalacaktır.`
                      : `Seçilen faturalar için işlem tutarının tamamı otomatik dağıtılacaktır.`}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="ml-1 text-[10px] font-black uppercase italic tracking-widest text-slate-400">
                Tarih
              </label>

              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                className="mt-1 w-full rounded-2xl border-none bg-slate-50 px-4 py-5 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-slate-100"
              />
            </div>

            <div className="md:col-span-2">
              <label className="ml-1 text-[10px] font-black uppercase italic tracking-widest text-slate-400">
                Açıklama
              </label>

              <textarea
                placeholder="İşlem açıklaması (Neden bu işlem yapılıyor?)"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                className="mt-1 w-full resize-none rounded-2xl border-none bg-slate-50 p-4 text-sm font-medium text-slate-600 outline-none focus:ring-2 focus:ring-slate-100"
                rows="3"
              />
            </div>

            <div className="md:col-span-2 grid gap-2">
              <button
                type="submit"
                className={`w-full rounded-2xl py-4 text-xs font-black tracking-[0.2em] text-white shadow-xl transition-all active:scale-[0.98] ${
                  formData.type === 'Inflow'
                    ? 'bg-emerald-600 shadow-emerald-200 hover:bg-emerald-700'
                    : 'bg-rose-600 shadow-rose-200 hover:bg-rose-700'
                } ${isTransfer ? 'bg-slate-900 shadow-slate-200 hover:bg-slate-800' : ''}`}
              >
                İŞLEMİ ONAYLA
              </button>

              <div className="relative min-h-5">
                {formError && (
                  <p className="absolute inset-x-0 top-0 text-center text-[10px] font-black uppercase tracking-widest text-rose-500">
                    {formError}
                  </p>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      <AccountSelectModal
        isOpen={isAccountSearchOpen}
        onClose={() => setIsAccountSearchOpen(false)}
        onSelect={handleAccountSelect}
        accountType={accountSelectType}
        title={accountSelectTitle}
        enableQuickCreate={!isPersonnelTransaction}
      />
    </>
  );
};

export default TransactionModal;
